/** Split a display name into forename + remainder surname. */
function splitDisplayName(display: string): { forename: string; surname: string } {
  const parts = display.trim().split(/\s+/).filter((part) => part.length > 0);
  if (parts.length === 0) {
    return { forename: '', surname: '' };
  }
  if (parts.length === 1) {
    return { forename: parts[0], surname: '' };
  }
  return {
    forename: parts[0],
    surname: parts.slice(1).join(' '),
  };
}

/**
 * Normalise SSO profile fields into separate forename / surname for registration.
 * Prefers explicit given + family names; otherwise splits a full display name.
 */
export function resolveSsoUserNames(source: {
  givenName?: string | null;
  familyName?: string | null;
  displayName?: string | null;
}): { forename: string; surname: string } {
  const given = (source.givenName ?? '').trim();
  const family = (source.familyName ?? '').trim();

  if (given && family) {
    return { forename: given, surname: family };
  }

  if (given && !family && !given.includes(' ')) {
    return { forename: given, surname: '' };
  }

  const displayCandidate =
    (given.includes(' ') ? given : '') ||
    (source.displayName ?? '').trim() ||
    given;

  if (displayCandidate) {
    return splitDisplayName(displayCandidate);
  }

  return { forename: given, surname: family };
}

/** MSAL account → forename / surname (uses id token claims when present). */
export function resolveMsalAccountNames(account: {
  name?: string | null;
  idTokenClaims?: Record<string, unknown> | null;
}): { forename: string; surname: string } {
  const claims = account.idTokenClaims ?? {};
  const givenName = typeof claims['given_name'] === 'string' ? claims['given_name'] : null;
  const familyName = typeof claims['family_name'] === 'string' ? claims['family_name'] : null;
  return resolveSsoUserNames({
    givenName,
    familyName,
    displayName: account.name,
  });
}
